import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ClientPhotoCollection from '../components/ClientPhotoCollection';
import AddPhotoToCollection from '../components/AddPhotoToCollection';
import LmHeader from '../components/LmHeader.jsx';
import LmFooter from '../components/LmFooter.jsx';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [client, setClient] = useState(null);
  const [isTransferModalVisible, setIsTransferModalVisible] = useState(false);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState(null);

  useEffect(() => {
    loadClientData();
    loadProduct();
  }, [id]);

  const loadClientData = () => {
    try {
      const clientData = localStorage.getItem('clientData');
      if (clientData) {
        const parsedClient = JSON.parse(clientData);
        setClient(parsedClient);
      }
    } catch (error) {
      console.error('Ошибка парсинга данных клиента:', error);
    }
  };

  const loadProduct = async () => {
    try {
      const token = localStorage.getItem('clientToken');
      if (!token) {
        navigate('/enter');
        return;
      }

      const response = await axios.get(`https://limitverify.ru:5000/api/client/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setProduct(response.data.product);

      // Дополнительная проверка безопасности
      if (client && response.data.product.clientId && client.id !== response.data.product.clientId) {
        console.warn('Security warning: Product does not belong to current client');
        setError('Доступ запрещен. Этот товар не принадлежит вам.');
        return;
      }
    } catch (error) {
      console.error('Ошибка загрузки изделия:', error);

      if (error.response?.status === 404) {
        setError('Изделие не найдено');
      } else if (error.response?.status === 401) {
        setError('Сессия истекла. Пожалуйста, войдите заново');
        setTimeout(() => navigate('/enter'), 2000);
      } else if (error.response?.status === 403) {
        setError('Доступ запрещен. Этот товар не принадлежит вам.');
      } else {
        setError('Ошибка загрузки изделия');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/cabinet');
  };

  const handleTransferProduct = async () => {
    if (!transferEmail || !transferEmail.includes('@')) {
      setTransferError('Введите корректный email адрес');
      return;
    }

    // Проверяем, что email не совпадает с email текущего клиента
    if (client && client.email && client.email.toLowerCase().trim() === transferEmail.toLowerCase().trim()) {
      setTransferError('Нельзя передать изделие самому себе');
      return;
    }

    setTransferLoading(true);
    setTransferError(null);

    try {
      const token = localStorage.getItem('clientToken');
      const response = await axios.post(
        'https://limitverify.ru:5000/api/client/transfer-product',
        {
          productId: product.id,
          email: transferEmail
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setIsTransferModalVisible(false);
        setTransferEmail('');
        // Перезагружаем данные изделия
        await loadProduct();
        // Показываем сообщение об успехе
        alert(response.data.message || 'Изделие успешно передано новому владельцу');
      }
    } catch (error) {
      console.error('Ошибка передачи изделия:', error);
      setTransferError(
        error.response?.data?.error ||
        'Ошибка при передаче изделия. Попробуйте еще раз.'
      );
    } finally {
      setTransferLoading(false);
    }
  };

  const statusClass = (status) => {
    switch (status) {
      case 'АКТИВНО': return 'lm-status--active';
      case 'АРХИВ': return 'lm-status--archived';
      case 'АННУЛИРОВАНО': return 'lm-status--canceled';
      default: return 'lm-status--archived';
    }
  };

  if (loading) {
    return (
      <div className="lm-page">
        <main className="lm-content lm-content--center">
          <span className="lm-text-2 lm-muted">Загрузка изделия...</span>
        </main>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="lm-page">
        <LmHeader logoTo="/cabinet" actions={[{ label: 'Назад', onClick: handleBack }]} />
        <main className="lm-content lm-content--center">
          <div className="lm-auth" style={{ alignItems: 'center', textAlign: 'center' }}>
            <span className="lm-text-2 lm-danger">{error || 'Изделие не найдено'}</span>
            <button type="button" className="lm-btn" onClick={handleBack}>Вернуться к списку</button>
          </div>
        </main>
        <LmFooter />
      </div>
    );
  }

  const photos = product.photos || [];
  const hasPhotos = photos.length > 0;

  return (
    <div className="lm-page">
      <LmHeader logoTo="/cabinet" actions={[{ label: 'Назад', onClick: handleBack }]} />

      <main className="lm-content">
        <h1 className="lm-h1">Детали изделия</h1>

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Левая колонка — фото + коллекция */}
          <div style={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 24 }}>
            <section className="lm-section" style={{ padding: 0, gap: 0, overflow: 'hidden' }}>
              <div
                style={{
                  position: 'relative',
                  height: 320,
                  background: 'var(--bg-page)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
              >
                {photos.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setSelectedPhotoIndex(prev => (prev === 0 ? photos.length - 1 : prev - 1))}
                      style={navBtnStyle('left')}
                      aria-label="Предыдущее фото"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedPhotoIndex(prev => (prev === photos.length - 1 ? 0 : prev + 1))}
                      style={navBtnStyle('right')}
                      aria-label="Следующее фото"
                    >
                      ›
                    </button>
                  </>
                )}

                {hasPhotos ? (
                  <img
                    src={photos[selectedPhotoIndex]?.photoUrl || product.photoUrl}
                    alt={product.name}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : product.photoUrl ? (
                  <img src={product.photoUrl} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <span className="lm-text-3 lm-muted">Нет фото</span>
                )}

                {photos.length > 1 && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      gap: 6,
                      padding: '6px 10px',
                      background: 'rgba(0,0,0,0.6)'
                    }}
                  >
                    {photos.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setSelectedPhotoIndex(index)}
                        aria-label={`Фото ${index + 1}`}
                        style={{
                          width: 8,
                          height: 8,
                          padding: 0,
                          border: 'none',
                          cursor: 'pointer',
                          background: selectedPhotoIndex === index ? 'var(--accent)' : 'rgba(255,255,255,0.5)'
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {photos.length > 1 && (
                <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: 16, borderTop: '1px solid var(--border-color)' }}>
                  {photos.map((photo, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setSelectedPhotoIndex(index)}
                      style={{
                        width: 56,
                        height: 56,
                        flexShrink: 0,
                        padding: 0,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        background: 'var(--bg-page)',
                        border: `1px solid ${selectedPhotoIndex === index ? 'var(--accent)' : 'var(--border-color)'}`
                      }}
                    >
                      <img src={photo.photoUrl} alt={`${product.name} ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="lm-section collection-manage">
              <h3 className="lm-h3">Управление фото в коллекции</h3>
              <AddPhotoToCollection
                productId={product.id}
                canEdit={product.canEdit}
                onPhotoAdded={() => {
                  if (window.refreshClientPhotos) window.refreshClientPhotos();
                  loadProduct();
                }}
              />
              <ClientPhotoCollection
                productId={product.id}
                canEdit={product.canEdit}
                onPhotoAdded={(refreshFn) => { window.refreshClientPhotos = refreshFn; }}
                onPhotoRemoved={() => {
                  if (window.refreshClientPhotos) window.refreshClientPhotos();
                  loadProduct();
                }}
              />
            </section>
          </div>

          {/* Правая колонка — сертификат + история */}
          <div style={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 24 }}>
            <section className="lm-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <h2 className="lm-h3">Сертификат подлинности</h2>
                <span className="lm-descriptor lm-muted">
                  {product.identificationNumber ? `№ ${product.identificationNumber}` : '№ ---'}
                </span>
              </div>

              <div className="lm-cert-table">
                <CertRow label="Изделие" value={product.name} />
                {product.serialNumber && <CertRow label="Серийный номер" value={product.serialNumber} />}
                <CertRow label="Идентификационный номер" value={product.identificationNumber || 'Не указан'} />
                <CertRow label="Размер" value={product.size || 'Не указан'} />
                <CertRow label="Цвет" value={product.color || 'Не указан'} />
                <CertRow label="Материал" value={product.material || 'Не указан'} />
                {product.features && <CertRow label="Особенности" value={product.features} />}
                <CertRow label="Место производства" value={product.productionLocation || 'Не указано'} />
                <CertRow label="Место выпуска" value={product.releaseLocation || 'Не указано'} />
                <CertRow label="Дата выпуска" value={product.releaseDate ? new Date(product.releaseDate).toLocaleDateString('ru-RU') : 'Не указана'} />
                {product.certificateDate && (
                  <CertRow label="Дата выдачи сертификата" value={new Date(product.certificateDate).toLocaleDateString('ru-RU')} />
                )}
                {product.description && <CertRow label="Описание" value={product.description} />}
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
                <span className={`lm-descriptor-2 ${statusClass(product.status)}`} style={{ border: '1px solid currentColor', padding: '6px 12px' }}>
                  {product.status}
                </span>
              </div>
            </section>

            {/* История владельцев */}
            {product.ownershipHistory && product.ownershipHistory.length > 0 && (
              <section className="lm-section">
                <h3 className="lm-h3">История владельцев</h3>
                <div className="lm-cert-table">
                  {product.ownershipHistory.map((owner, index) => (
                    <div key={index} className="lm-cert-row">
                      <span className="lm-cert-row__label">
                        Владелец №{owner.ownerNumber}
                      </span>
                      <span className="lm-cert-row__value">
                        {owner.clientName || 'Неизвестно'}
                        {' · '}
                        {new Date(owner.startDate).toLocaleDateString('ru-RU')}
                        {' — '}
                        {owner.endDate ? new Date(owner.endDate).toLocaleDateString('ru-RU') : 'настоящее время'}
                      </span>
                    </div>
                  ))}
                </div>

                {product.isCurrentOwner && (
                  <button
                    type="button"
                    className="lm-btn lm-btn--block"
                    onClick={() => setIsTransferModalVisible(true)}
                    disabled={product.ownershipHistory.length >= 3}
                  >
                    {product.ownershipHistory.length >= 3 ? 'Лимит передачи' : 'Передать изделие'}
                  </button>
                )}
              </section>
            )}

            {/* Кнопка передачи (истории нет) */}
            {product.isCurrentOwner && (!product.ownershipHistory || product.ownershipHistory.length === 0) && (
              <section className="lm-section">
                <button type="button" className="lm-btn lm-btn--block" onClick={() => setIsTransferModalVisible(true)}>
                  Передать изделие
                </button>
              </section>
            )}
          </div>
        </div>
      </main>

      <LmFooter />

      {/* Модальное окно передачи изделия */}
      {isTransferModalVisible && (
        <div className="lm-modal-overlay">
          <div className="lm-modal" style={{ maxWidth: 500 }}>
            <button
              type="button"
              className="lm-modal__close"
              onClick={() => {
                setIsTransferModalVisible(false);
                setTransferEmail('');
                setTransferError(null);
              }}
              aria-label="Закрыть"
            >
              ✕
            </button>
            <h3 className="lm-modal__title">Передать изделие новому владельцу</h3>

            <div className="lm-field">
              <span className="lm-field__label">Email нового владельца</span>
              <input
                type="email"
                className="lm-field__input"
                value={transferEmail}
                onChange={(e) => {
                  setTransferEmail(e.target.value);
                  setTransferError(null);
                }}
                placeholder="example@email.com"
                disabled={transferLoading}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !transferLoading) {
                    handleTransferProduct();
                  }
                }}
              />
              {transferError && <span className="lm-descriptor lm-danger">{transferError}</span>}
            </div>

            <p className="lm-descriptor lm-muted" style={{ margin: 0 }}>
              Если у указанного email нет аккаунта, он будет создан автоматически. Новому владельцу будет отправлено письмо с данными для входа.
            </p>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="lm-btn lm-btn--danger"
                onClick={() => {
                  setIsTransferModalVisible(false);
                  setTransferEmail('');
                  setTransferError(null);
                }}
                disabled={transferLoading}
              >
                Отмена
              </button>
              <button type="button" className="lm-btn" onClick={handleTransferProduct} disabled={transferLoading || !transferEmail}>
                {transferLoading ? 'Обработка...' : 'Передать'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const navBtnStyle = (side) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  [side]: 12,
  width: 36,
  height: 36,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0,0,0,0.6)',
  color: '#fff',
  border: '1px solid var(--border-color)',
  cursor: 'pointer',
  fontSize: 18,
  zIndex: 10
});

const CertRow = ({ label, value }) => (
  <div className="lm-cert-row">
    <span className="lm-cert-row__label">{label}</span>
    <span className="lm-cert-row__value">{value}</span>
  </div>
);

export default ProductDetail;
